/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useRef, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-lodash-debounce';
// @mui
import {
    Dialog,
    Box,
    TextField,
    InputAdornment,
    Typography,
    List,
    ListItemButton,
    CircularProgress,
    Divider,
    Chip,
    alpha,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import { useSnackbar } from 'notistack';
// components
import Iconify from './Iconify';
// utils
import { getStatusButton } from '../utils/getStatusButton';
import { fToNow } from '../utils/formatTime';
// store
import { universalSearch, clearApiData, searchCampaigns } from '../redux/slices/lead';
import { toggleUserEnabled, resetUserPassword } from '../redux/slices/practiceUser';
import { openCreateLeadDialog } from '../redux/slices/createLeadDialog';
import { useSelector, dispatch } from '../redux/store';
import { callApi } from '../utils/apiCall';
import { filterCommands, GROUP_LABELS } from '../constants/commandRegistry';

// Recent items storage key
const RECENT_ITEMS_KEY = 'command-palette-recent';
const RECENT_SEARCHES_KEY = 'command-palette-searches';
const PINNED_LEADS_KEY = 'command-palette-pinned-leads';
const MAX_RECENT_ITEMS = 5;
const MAX_RECENT_SEARCHES = 5;
const MAX_PINNED_LEADS = 10;
const ONBOARDING_KEY = 'command-palette-onboarded';

// Discovery tips shown on first use (like iOS Spotlight)
const DISCOVERY_TIPS = [
    {
        icon: 'eva:search-fill',
        title: 'Search everything',
        description: 'Find leads, treatments, appointments, staff, and more',
    },
    {
        icon: 'eva:arrow-ios-forward-fill',
        title: 'Quick actions',
        description: 'Select a result, then press → to WhatsApp, book, add notes',
    },
    {
        icon: 'eva:star-fill',
        title: 'Pin favourites',
        description: 'Star your frequent leads so they appear here instantly',
    },
    {
        icon: 'eva:hash-fill',
        title: 'Filter by type',
        description: 'Type lead: treat: appt: task: wa: to narrow your search',
    },
];

// Category prefix mapping for filtered search
const CATEGORY_PREFIXES = {
    'lead:': 'leads',
    'treat:': 'treatments',
    'appt:': 'appointments',
    'task:': 'tasks',
    'wa:': 'conversations',
    'note:': 'notes',
    'staff:': 'staff',
};

// Category icon mapping
const CATEGORY_ICONS = {
    leads: 'eva:person-fill',
    treatments: 'eva:heart-fill',
    appointments: 'eva:calendar-fill',
    tasks: 'fluent:tasks-app-28-regular',
    conversations: 'eva:message-circle-fill',
    notes: 'eva:edit-fill',
    staff: 'eva:people-fill',
    campaigns: 'eva:email-fill',
};

// Category display labels
const CATEGORY_LABELS = {
    leads: 'Leads',
    treatments: 'Treatments',
    appointments: 'Appointments',
    tasks: 'Tasks',
    conversations: 'Conversations',
    notes: 'Notes',
    staff: 'Staff',
    campaigns: 'Campaigns',
};

// Lead journey stages — mirrors JOURNEY_STAGES from LeadDetailConst
// Used for status-aware actions in the action panel (Option B: navigate)
// Designed for easy upgrade to Option A (inline quick actions) later
const LEAD_JOURNEY_STAGES = [
    { value: 'NEW', label: 'Enquiry', color: '#3b82f6' },
    { value: 'CONSULTATION_BOOKED', label: 'Booked', color: '#f59e0b' },
    { value: 'CONSULTATION_ATTENDED', label: 'Attended', color: '#8b5cf6' },
    { value: 'TREATMENT_STARTED', label: 'Treatment', color: '#00A76F' },
    { value: 'CLOSED', label: 'Complete', color: '#10b981' },
];
const LEAD_STAGE_ORDER = ['NEW', 'CONSULTATION_BOOKED', 'CONSULTATION_ATTENDED', 'TREATMENT_STARTED', 'CLOSED'];

const getLeadStageIndex = (status) => {
    const idx = LEAD_STAGE_ORDER.indexOf(status);
    return idx >= 0 ? idx : -1;
};

const getLeadCurrentStatus = (treatments) => {
    if (!treatments || treatments.length === 0) return 'NEW';
    let maxIdx = 0;
    treatments.forEach((t) => {
        const idx = LEAD_STAGE_ORDER.indexOf(t.status);
        if (idx > maxIdx) maxIdx = idx;
    });
    return LEAD_STAGE_ORDER[maxIdx] || 'NEW';
};

const getLeadStage = (status) =>
    LEAD_JOURNEY_STAGES.find((s) => s.value === status) || LEAD_JOURNEY_STAGES[0];

// Helper: Extract category prefix from query
const extractCategoryPrefix = (query) => {
    const trimmed = query.trim();
    const match = Object.entries(CATEGORY_PREFIXES).find(([prefix]) => trimmed.startsWith(prefix));
    if (match) {
        return { category: match[1], query: trimmed.slice(match[0].length).trim() };
    }
    return { category: null, query: trimmed };
};

// Helper: Get category icon
const getCategoryIcon = (category) => CATEGORY_ICONS[category] || 'eva:search-fill';

// Helper: Get category display label
const getCategoryLabel = (category) => CATEGORY_LABELS[category] || category;

// Helper: Flatten all results across categories for keyboard navigation
const flattenResultsForKeyboard = (categorizedResults) => {
    const flat = [];
    Object.entries(categorizedResults).forEach(([category, results]) => {
        if (Array.isArray(results) && results.length > 0) {
            results.forEach(result => {
                flat.push({ ...result, __category: category });
            });
        }
    });
    return flat;
};

const CommandPalette = ({ open, onClose }) => {
    const navigate = useNavigate();
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const inputRef = useRef(null);
    const handleNavigateRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [recentItems, setRecentItems] = useState([]);
    const [recentSearches, setRecentSearches] = useState([]);
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [pinnedLeads, setPinnedLeads] = useState([]);
    const [actionPanelOpen, setActionPanelOpen] = useState(false);
    const [actionPanelIndex, setActionPanelIndex] = useState(0);
    const [chipIndex, setChipIndex] = useState(0);
    // Contextual sub-panel state (depth 1 = sub-list, depth 2 = sub-item actions)
    const [panelDepth, setPanelDepth] = useState(0);
    const [panelSubItems, setPanelSubItems] = useState([]);
    const [panelSubIndex, setPanelSubIndex] = useState(0);
    const [panelSubLoading, setPanelSubLoading] = useState(false);
    const [panelSubSelected, setPanelSubSelected] = useState(null);
    const [showOnboarding, setShowOnboarding] = useState(false);
    // Command mode: triggered when input starts with /
    const isCommandMode = searchQuery.startsWith('/');
    const commandQuery = isCommandMode ? searchQuery.slice(1) : '';

    const filteredCommands = useMemo(
        () => (isCommandMode ? filterCommands(commandQuery) : []),
        [isCommandMode, commandQuery]
    );

    // Group filtered commands for display
    const groupedCommands = useMemo(
        () =>
            isCommandMode
                ? Object.entries(
                      filteredCommands.reduce((acc, cmd) => {
                          if (!acc[cmd.group]) acc[cmd.group] = [];
                          acc[cmd.group].push(cmd);
                          return acc;
                      }, {})
                  )
                : [],
        [isCommandMode, filteredCommands]
    );

    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { searchedLead, universalSearchResults, isLoading } = useSelector((state) => state.lead);
    const staffList = useSelector((state) => state.practiceUser.practiceUser.results || []);
    const practiceHasWhatsapp = Boolean(useSelector((state) => state.whatsapp?.practiceMetaData?.whatsAppAccountId));
    const toDoNotificationData = useSelector((state) => state.toDoNotifications?.toDoNotificationData || []);
    const whatsappUnreadCount = useSelector((state) => state.whatsappV2?.filterCounts?.FOLLOW_UP_COUNT || 0);

    const overdueTaskCount = useMemo(() => {
        const now = new Date();
        return toDoNotificationData.filter((task) => {
            if (task.isCompleted) return false;
            if (!task.dateTime) return false;
            return new Date(task.dateTime) < now;
        }).length;
    }, [toDoNotificationData]);

    const debouncedValue = useDebounce(searchQuery, 300);

    // Extract category prefix if present
    const { category: activeCategory, query: cleanQuery } = extractCategoryPrefix(searchQuery);

    // Load recent items and searches from localStorage
    useEffect(() => {
        try {
            const stored = localStorage.getItem(RECENT_ITEMS_KEY);
            if (stored) {
                const items = JSON.parse(stored);
                setRecentItems(items.filter(r => r.type === 'lead'));
            }
        } catch (error) {
            console.error('Failed to load recent items:', error);
        }

        try {
            const storedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
            if (storedSearches) {
                setRecentSearches(JSON.parse(storedSearches));
            }
        } catch (error) {
            console.error('Failed to load recent searches:', error);
        }

        try {
            const storedPinned = localStorage.getItem(PINNED_LEADS_KEY);
            if (storedPinned) {
                setPinnedLeads(JSON.parse(storedPinned));
            }
        } catch (error) {
            console.error('Failed to load pinned leads:', error);
        }

        // Check if user has seen onboarding
        try {
            const onboarded = localStorage.getItem(ONBOARDING_KEY);
            if (!onboarded) {
                setShowOnboarding(true);
            }
        } catch (error) {
            // Ignore localStorage errors
        }
    }, []);

    // Dismiss onboarding and mark as seen
    const dismissOnboarding = useCallback(() => {
        setShowOnboarding(false);
        try {
            localStorage.setItem(ONBOARDING_KEY, 'true');
        } catch (error) {
            // Ignore localStorage errors
        }
    }, []);

    // Clear search when modal closes
    useEffect(() => {
        if (!open) {
            setSearchQuery('');
            dispatch(clearApiData());
            setSelectedIndex(0);
            setActionPanelOpen(false);
            setActionPanelIndex(0);
            setChipIndex(0);
        }
    }, [open]);

    // Search when debounced query changes - use universal search
    useEffect(() => {
        if (debouncedValue.startsWith('/')) return; // command mode — no API search
        if (debouncedValue.length > 0 && practiceId) {
            const { category, query } = extractCategoryPrefix(debouncedValue);
            let search = query;

            // Phone number normalization (UK)
            if (Number(search) && !search.includes('+')) {
                search = `+44${search}`;
            }

            // Use universal search
            dispatch(universalSearch(search, practiceId, category));

            // Also search campaigns (supplementary — silent fail)
            if (!category || category === 'campaigns') {
                dispatch(searchCampaigns(search, practiceId));
            }

            // Save to recent searches when search completes
            saveRecentSearch(debouncedValue);
        } else if (debouncedValue.length === 0) {
            dispatch(clearApiData());
        }
    }, [debouncedValue, practiceId]);

    // Save recent search
    const saveRecentSearch = useCallback((query) => {
        if (!query || query.trim().length === 0) return;

        try {
            const recent = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
            const filtered = recent.filter(r => r.query !== query);
            const updated = [{ query, timestamp: Date.now() }, ...filtered].slice(0, MAX_RECENT_SEARCHES);
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
            setRecentSearches(updated);
        } catch (error) {
            console.error('Failed to save recent search:', error);
        }
    }, []);

    // Remove recent search
    const removeRecentSearch = useCallback((query) => {
        try {
            const recent = JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
            const updated = recent.filter(r => r.query !== query);
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
            setRecentSearches(updated);
        } catch (error) {
            console.error('Failed to remove recent search:', error);
        }
    }, []);

    // Clear all recent searches
    const clearAllRecentSearches = useCallback(() => {
        try {
            localStorage.removeItem(RECENT_SEARCHES_KEY);
            setRecentSearches([]);
        } catch (error) {
            console.error('Failed to clear recent searches:', error);
        }
    }, []);

    // Remove a single recently viewed item
    const removeRecentItem = useCallback((itemId) => {
        try {
            const recent = JSON.parse(localStorage.getItem(RECENT_ITEMS_KEY) || '[]');
            const updated = recent.filter(r => r.id !== itemId);
            localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
            setRecentItems(updated.filter(r => r.type === 'lead'));
        } catch (error) {
            console.error('Failed to remove recent item:', error);
        }
    }, []);

    // Clear all recently viewed items
    const clearAllRecentItems = useCallback(() => {
        try {
            localStorage.removeItem(RECENT_ITEMS_KEY);
            setRecentItems([]);
        } catch (error) {
            console.error('Failed to clear recent items:', error);
        }
    }, []);

    // Save recent item with timestamp
    const saveRecentItem = useCallback((item) => {
        try {
            const recent = JSON.parse(localStorage.getItem(RECENT_ITEMS_KEY) || '[]');
            const filtered = recent.filter(r => r.id !== item.id);
            const itemWithTimestamp = { ...item, timestamp: Date.now() };
            const updated = [itemWithTimestamp, ...filtered].slice(0, MAX_RECENT_ITEMS);
            localStorage.setItem(RECENT_ITEMS_KEY, JSON.stringify(updated));
            setRecentItems(updated.filter(r => r.type === 'lead'));
        } catch (error) {
            console.error('Failed to save recent item:', error);
        }
    }, []);

    // Check if a lead is pinned
    const isPinnedLead = useCallback((leadId) => pinnedLeads.some(
        (p) => p.id === leadId || p.leadId === leadId
    ), [pinnedLeads]);

    // Toggle pin/unpin a lead
    const togglePinLead = useCallback((lead, e) => {
        if (e) e.stopPropagation();
        try {
            const leadId = lead.leadId || lead.lead_id || lead.id;
            const current = JSON.parse(localStorage.getItem(PINNED_LEADS_KEY) || '[]');
            const isAlreadyPinned = current.some((p) => p.id === leadId || p.leadId === leadId);

            let updated;
            if (isAlreadyPinned) {
                updated = current.filter((p) => p.id !== leadId && p.leadId !== leadId);
            } else {
                const newPin = {
                    id: leadId,
                    first_name: lead.first_name || lead.title || '',
                    last_name: lead.last_name || '',
                    status: lead.status,
                    lead_contact: lead.lead_contact,
                    lead_treatments: lead.lead_treatments,
                    pinnedAt: Date.now(),
                };
                updated = [newPin, ...current].slice(0, MAX_PINNED_LEADS);
            }

            localStorage.setItem(PINNED_LEADS_KEY, JSON.stringify(updated));
            setPinnedLeads(updated);
        } catch (error) {
            console.error('Failed to toggle pinned lead:', error);
        }
    }, [pinnedLeads]);

    // Process search results - handle API response structure
    const apiResponse = universalSearchResults || {};
    const categorizedResults = apiResponse.results || {};
    // Flatten results for keyboard navigation
    let flatResults;
    if (universalSearchResults) {
        flatResults = flattenResultsForKeyboard(categorizedResults);
    } else if (Array.isArray(searchedLead) && searchedLead.length > 0) {
        flatResults = searchedLead.map(lead => ({ ...lead, type: 'lead', __category: 'leads' }));
    } else {
        flatResults = [];
    }

    // Get actions for a given item and category (for the action panel)
    // For leads: status-aware forward actions first, then generic actions
    // Designed for Option B (navigate) — action.mode can be upgraded to 'inline' for Option A
    const getItemActions = useCallback((item, category) => {
        const leadId = item.leadId || item.lead_id || item.id;
        const threadId = item.thread_id || item.whatsapp_thread_id;

        switch (category) {
            case 'leads':
            case 'lead': {
                const treatments = item.lead_treatments || [];
                const currentStatus = treatments.length > 0 ? getLeadCurrentStatus(treatments) : (item.status || 'NEW');
                const stageIdx = getLeadStageIndex(currentStatus);
                const navigateToLead = (stateOverride) => {
                    navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                        state: stateOverride,
                    });
                    onClose();
                };

                const actions = [];

                // ── Forward actions (status-driven) ──
                // These mirror getForwardActions() in TaskSidebar
                if (stageIdx < 1) {
                    actions.push({
                        icon: 'solar:calendar-bold-duotone',
                        label: 'Book Consultation',
                        color: '#0078FF',
                        section: 'next',
                        onClick: () => navigateToLead({ shouldRedirectToOverview: true, autoAction: 'bookConsultation' }),
                    });
                }
                if (currentStatus === 'CONSULTATION_BOOKED' || currentStatus === 'FAILED_TO_ATTEND_CONSULTATION') {
                    actions.push({
                        icon: 'solar:check-circle-bold-duotone',
                        label: 'Mark Attended',
                        color: '#22C55E',
                        section: 'next',
                        onClick: () => navigateToLead({ shouldRedirectToOverview: true, autoAction: 'markAttended' }),
                    });
                    actions.push({
                        icon: 'solar:close-circle-bold-duotone',
                        label: 'FTA',
                        color: '#FF5630',
                        section: 'next',
                        onClick: () => navigateToLead({ shouldRedirectToOverview: true, autoAction: 'markFTA' }),
                    });
                }
                if (stageIdx >= 1 && stageIdx < 3) {
                    actions.push({
                        icon: 'solar:heart-pulse-bold-duotone',
                        label: 'Start Treatment',
                        color: '#FFAB00',
                        section: 'next',
                        onClick: () => navigateToLead({ shouldRedirectToOverview: true, autoAction: 'startTreatment' }),
                    });
                }
                if (stageIdx >= 1 && stageIdx < 4) {
                    actions.push({
                        icon: 'solar:verified-check-bold-duotone',
                        label: 'Complete Treatment',
                        color: '#00A76F',
                        section: 'next',
                        onClick: () => navigateToLead({ shouldRedirectToOverview: true, autoAction: 'completeTreatment' }),
                    });
                }

                // ── Generic actions ──
                actions.push({
                    icon: 'eva:person-fill',
                    label: 'View Lead Overview',
                    section: 'more',
                    onClick: () => navigateToLead({ shouldRedirectToOverview: true }),
                });

                if (practiceHasWhatsapp) {
                    actions.push({
                        icon: 'eva:message-circle-fill',
                        label: 'Send WhatsApp',
                        section: 'more',
                        onClick: () => {
                            const whatsappUrl = threadId
                                ? `/dashboard/practice/${practiceId}/whatsapp/thread/${threadId}`
                                : `/dashboard/practice/${practiceId}/whatsapp?lead=${leadId}`;
                            navigate(whatsappUrl);
                            onClose();
                        },
                    });
                }

                actions.push({
                    icon: 'eva:file-text-fill',
                    label: 'Add Note',
                    section: 'more',
                    onClick: () => navigateToLead({ shouldRedirectToOverview: false, tab: 5 }),
                });

                actions.push({
                    icon: 'eva:checkmark-square-fill',
                    label: 'View Tasks',
                    section: 'more',
                    onClick: () => navigateToLead({ goToTaskTab: true }),
                });

                actions.push({
                    icon: isPinnedLead(leadId) ? 'eva:star-fill' : 'eva:star-outline',
                    label: isPinnedLead(leadId) ? 'Unpin Lead' : 'Pin Lead',
                    section: 'more',
                    onClick: () => togglePinLead(item),
                });

                if (!item.is_duplicate) {
                    actions.push({
                        icon: 'eva:copy-fill',
                        label: 'Mark as Duplicate',
                        color: '#FFAB00',
                        section: 'more',
                        onClick: async () => {
                            try {
                                await callApi(
                                    `/api/v1/practices/${practiceId}/leads/${leadId}/mark_as_duplicate/`,
                                    'post'
                                );
                                enqueueSnackbar('Lead marked as duplicate', { variant: 'success' });
                            } catch (err) {
                                enqueueSnackbar('Failed to mark as duplicate', { variant: 'error' });
                            }
                        },
                    });
                }

                return actions;
            }
            case 'treatments':
                return [
                    {
                        icon: 'eva:edit-2-fill',
                        label: 'Change Status',
                        onClick: () => {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                ];
            case 'appointments':
                return [
                    {
                        icon: 'eva:checkmark-circle-fill',
                        label: 'Mark Attended',
                        onClick: () => {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:close-circle-fill',
                        label: 'Mark FTA',
                        onClick: () => {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:calendar-fill',
                        label: 'Reschedule',
                        onClick: () => {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                ];
            case 'staff': {
                const staffActions = [
                    {
                        icon: 'eva:person-fill',
                        label: 'View Their Leads',
                        section: 'more',
                        onClick: () => {
                            navigate('/dashboard/tables/all-lead-table');
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:bar-chart-fill',
                        label: 'View Performance',
                        section: 'more',
                        onClick: () => {
                            navigate('/dashboard/analytics-reports/by-practitioner');
                            onClose();
                        },
                    },
                ];
                if (item.is_enabled !== false) {
                    staffActions.push({
                        icon: 'eva:person-delete-fill',
                        label: 'Disable User',
                        color: '#FF5630',
                        section: 'more',
                        onClick: async () => {
                            try {
                                await dispatch(toggleUserEnabled(practiceId, item.id, false));
                                enqueueSnackbar('User disabled', { variant: 'success' });
                            } catch (err) {
                                enqueueSnackbar('Failed to disable user', { variant: 'error' });
                            }
                        },
                    });
                } else {
                    staffActions.push({
                        icon: 'eva:person-add-fill',
                        label: 'Enable User',
                        color: '#00AB55',
                        section: 'more',
                        onClick: async () => {
                            try {
                                await dispatch(toggleUserEnabled(practiceId, item.id, true));
                                enqueueSnackbar('User enabled', { variant: 'success' });
                            } catch (err) {
                                enqueueSnackbar('Failed to enable user', { variant: 'error' });
                            }
                        },
                    });
                }
                staffActions.push({
                    icon: 'eva:email-fill',
                    label: 'Reset Password',
                    section: 'more',
                    onClick: async () => {
                        try {
                            await dispatch(resetUserPassword(practiceId, item.email));
                            enqueueSnackbar('Password reset email sent', { variant: 'success' });
                        } catch (err) {
                            enqueueSnackbar('Failed to send reset email', { variant: 'error' });
                        }
                    },
                });
                return staffActions;
            }
            case 'tasks': {
                const tomorrow9am = new Date();
                tomorrow9am.setDate(tomorrow9am.getDate() + 1);
                tomorrow9am.setHours(9, 0, 0, 0);

                const taskActions = [];
                if (!item.is_completed) {
                    taskActions.push({
                        icon: 'eva:checkmark-square-fill',
                        label: 'Mark Complete',
                        color: '#00AB55',
                        onClick: async () => {
                            try {
                                await callApi(
                                    `/api/v1/practices/${practiceId}/reminders/${item.id}/`,
                                    'patch',
                                    { is_completed: true }
                                );
                                enqueueSnackbar('Task marked complete', { variant: 'success' });
                            } catch (err) {
                                enqueueSnackbar('Failed to complete task', { variant: 'error' });
                            }
                        },
                    });
                }
                taskActions.push({
                    icon: 'eva:clock-fill',
                    label: 'Snooze 1 Day',
                    onClick: async () => {
                        try {
                            const pad = (n) => String(n).padStart(2, '0');
                            const d = tomorrow9am;
                            const formatted = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:00.0Z`;
                            await callApi(
                                `/api/v1/practices/${practiceId}/reminders/${item.id}/`,
                                'patch',
                                { datetime: formatted }
                            );
                            enqueueSnackbar('Task snoozed to tomorrow 9am', { variant: 'success' });
                        } catch (err) {
                            enqueueSnackbar('Failed to snooze task', { variant: 'error' });
                        }
                    },
                });
                return taskActions;
            }
            case 'campaigns': {
                const campaignActions = [];
                const status = (item.status || '').toUpperCase();
                if (status === 'LIVE' || status === 'SCHEDULED') {
                    campaignActions.push({
                        icon: 'eva:close-circle-fill',
                        label: 'Cancel Campaign',
                        color: '#FF5630',
                        onClick: async () => {
                            try {
                                await callApi(
                                    `/api/v1/practices/${practiceId}/campaigns/${item.id}/cancel/`,
                                    'post'
                                );
                                enqueueSnackbar('Campaign cancelled', { variant: 'success' });
                            } catch (err) {
                                enqueueSnackbar('Failed to cancel campaign', { variant: 'error' });
                            }
                        },
                    });
                }
                campaignActions.push({
                    icon: 'eva:bar-chart-fill',
                    label: 'View Report',
                    onClick: () => {
                        navigate(`/dashboard/campaigns-manager/campaigns/${item.id}/report`);
                        onClose();
                    },
                });
                return campaignActions;
            }
            default:
                return [];
        }
    }, [navigate, practiceId, onClose, practiceHasWhatsapp, isPinnedLead, togglePinLead, enqueueSnackbar]);

    // Get needs attention status and color
    const getNeedsAttentionStatus = useCallback((item) => {
        const issues = [];
        let color = null;

        if (item.needs_attention) {
            if (item.needs_attention.fta || item.needs_attention.unpaid_invoice) {
                color = 'error.main';
                if (item.needs_attention.fta) issues.push('Failed to attend');
                if (item.needs_attention.unpaid_invoice) issues.push('Unpaid invoice');
            } else if (item.needs_attention.overdue_followup) {
                color = 'warning.main';
                issues.push('Overdue follow-up');
            }
        }

        return { hasIssues: issues.length > 0, issues, color };
    }, []);

    // Render needs attention badge
    const renderNeedsAttentionBadge = useCallback((item) => {
        const { hasIssues, issues, color } = getNeedsAttentionStatus(item);
        if (!hasIssues) return null;

        return (
            <Tooltip title={issues.join(', ')}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: -2,
                        right: -2,
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: color,
                        border: '2px solid white',
                        zIndex: 1,
                    }}
                />
            </Tooltip>
        );
    }, [getNeedsAttentionStatus]);

    // Render last contacted timestamp
    const renderLastContacted = useCallback((lead) => {
        const lastContacted = lead.last_contacted_at;

        if (!lastContacted) {
            return (
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'warning.main',
                        lineHeight: 1.4,
                    }}
                >
                    Not contacted yet
                </Typography>
            );
        }

        try {
            return (
                <Typography
                    sx={{
                        fontSize: '11px',
                        color: 'text.secondary',
                        lineHeight: 1.4,
                    }}
                >
                    Last contacted: {fToNow(lastContacted)}
                </Typography>
            );
        } catch (error) {
            return null;
        }
    }, []);

    // Render right-arrow hint on keyboard-selected items to indicate action panel
    const renderActionHint = useCallback((isSelected) => {
        if (!isSelected) return null;
        return (
            <Tooltip title="Press → for actions" placement="left">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        ml: 0.5,
                        color: 'text.disabled',
                        animation: 'fadeInHint 300ms ease',
                        '@keyframes fadeInHint': {
                            from: { opacity: 0, transform: 'translateX(-4px)' },
                            to: { opacity: 1, transform: 'translateX(0)' },
                        },
                    }}
                >
                    <Iconify
                        icon="eva:chevron-right-fill"
                        sx={{ width: 18, height: 18 }}
                    />
                </Box>
            </Tooltip>
        );
    }, []);

    // Render quick actions on hover
    const renderQuickActions = useCallback((item, category, index) => {
        const isHovered = hoveredIndex === index;
        if (!isHovered) return null;

        const leadId = item.leadId || item.lead_id || item.id;
        let actions = [];

        switch (category) {
            case 'leads':
                actions = [];
                if (practiceHasWhatsapp) {
                    actions.push({
                        icon: 'eva:message-circle-fill',
                        label: 'WhatsApp',
                        onClick: (e) => {
                            e.stopPropagation();
                            const threadId = item.thread_id || item.whatsapp_thread_id;
                            const whatsappUrl = threadId
                                ? `/dashboard/practice/${practiceId}/whatsapp/thread/${threadId}`
                                : `/dashboard/practice/${practiceId}/whatsapp?lead=${leadId}`;
                            navigate(whatsappUrl);
                            onClose();
                        },
                    });
                }
                actions.push(
                    {
                        icon: 'eva:calendar-fill',
                        label: 'Book',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:file-text-fill',
                        label: 'Note',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 5 },
                            });
                            onClose();
                        },
                    },
                );
                break;
            case 'treatments':
                actions = [
                    {
                        icon: 'eva:edit-2-fill',
                        label: 'Change status',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                ];
                break;
            case 'appointments':
                actions = [
                    {
                        icon: 'eva:checkmark-circle-fill',
                        label: 'Attended',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:close-circle-fill',
                        label: 'FTA',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:calendar-fill',
                        label: 'Reschedule',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                            onClose();
                        },
                    },
                ];
                break;
            case 'staff':
                actions = [
                    {
                        icon: 'eva:person-fill',
                        label: 'View their leads',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate('/dashboard/tables/all-lead-table');
                            onClose();
                        },
                    },
                    {
                        icon: 'eva:bar-chart-fill',
                        label: 'View performance',
                        onClick: (e) => {
                            e.stopPropagation();
                            navigate('/dashboard/analytics-reports/overview');
                            onClose();
                        },
                    },
                ];
                break;
            default:
                return null;
        }

        return (
            <Box
                sx={{
                    display: 'flex',
                    gap: 0.5,
                    opacity: isHovered ? 1 : 0,
                    transition: 'opacity 150ms',
                }}
            >
                {actions.map((action, idx) => (
                    <Tooltip key={idx} title={action.label}>
                        <IconButton
                            size="small"
                            onClick={action.onClick}
                            sx={{
                                width: 28,
                                height: 28,
                                '& svg': { width: 18, height: 18 },
                            }}
                        >
                            <Iconify icon={action.icon} />
                        </IconButton>
                    </Tooltip>
                ))}
            </Box>
        );
    }, [hoveredIndex, navigate, practiceId, onClose, practiceHasWhatsapp]);

    // Show recent leads when no search query
    const showRecent = searchQuery.length === 0 && recentItems.length > 0;

    // Check if we have any results
    const hasResults = flatResults.length > 0;
    const hasCategories = universalSearchResults && Object.keys(categorizedResults).some(
        cat => Array.isArray(categorizedResults[cat]) && categorizedResults[cat].length > 0
    );

    // Build unified navigable items list across ALL visible sections
    const navigableItems = useMemo(() => {
        if (isCommandMode) {
            return filteredCommands.map(cmd => ({ ...cmd, __navType: 'command' }));
        }
        if (searchQuery.length > 0) {
            // When searching, navigable items are the flat search results
            return flatResults.map(item => ({
                ...item,
                __navType: 'result',
                __navCategory: item.__category || item.type || 'leads',
            }));
        }
        // When idle (no search), combine: recent searches (as ONE group) + pinned leads + recently viewed
        const items = [];
        const visibleSearches = recentSearches.slice(0, MAX_RECENT_SEARCHES);
        if (visibleSearches.length > 0) {
            items.push({
                __navType: 'recentSearchGroup',
                __navCategory: 'recentSearch',
                __navId: 'search-group',
                searches: visibleSearches,
            });
        }
        pinnedLeads.forEach(pinned => {
            items.push({
                ...pinned,
                __navType: 'pinnedLead',
                __navCategory: 'leads',
                __navId: `pinned-${pinned.id}`,
            });
        });
        recentItems.forEach(item => {
            items.push({
                ...item,
                __navType: 'recentItem',
                __navCategory: item.__category || item.type || 'leads',
                __navId: `recent-${item.id}`,
            });
        });
        return items;
    }, [isCommandMode, filteredCommands, searchQuery, flatResults, recentSearches, pinnedLeads, recentItems]);

    // Derive the currently selected navigable item
    const selectedNavItem = navigableItems[selectedIndex] || null;

    // Handle navigation — supports all result types
    const handleNavigate = useCallback((item) => {
        // Extract category from item
        const category = item.__category || item.type || 'leads';

        // Staff always navigates to practice-user page regardless of backend URL
        if (category === 'staff') {
            navigate('/dashboard/user/practice-user');
            onClose();
            return;
        }

        // Navigate based on category and provided URL
        if (item.url) {
            // Backend provides full navigation URL
            navigate(item.url);
        } else {
            // Fallback: construct URL based on category
            switch (category) {
                case 'lead':
                case 'leads': {
                    const leadId = item.leadId || item.id;
                    const isArchived = item.isArchived ?? item.is_archived;

                    const recentItem = {
                        id: `lead-${leadId}`,
                        title: `${item.first_name || item.title || ''} ${item.last_name || ''}`.trim(),
                        type: 'lead',
                        leadId,
                        isArchived,
                    };
                    saveRecentItem(recentItem);

                    if (isArchived) {
                        navigate(`practice/${practiceId}/archived/${leadId}`);
                    } else {
                        navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                            state: { shouldRedirectToOverview: true },
                        });
                    }
                    break;
                }
                case 'treatments': {
                    // Navigate to lead with treatment tab
                    const leadId = item.lead?.id || item.lead_id;
                    if (leadId) {
                        const isArchived = item.lead?.is_archived;
                        if (isArchived) {
                            navigate(`practice/${practiceId}/archived/${leadId}`);
                        } else {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                        }
                    }
                    break;
                }
                case 'appointments': {
                    // Navigate to lead with appointments in treatments tab
                    const leadId = item.lead?.id || item.lead_id;
                    if (leadId) {
                        const isArchived = item.lead?.is_archived;
                        if (isArchived) {
                            navigate(`practice/${practiceId}/archived/${leadId}`);
                        } else {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 3 },
                            });
                        }
                    }
                    break;
                }
                case 'tasks': {
                    // Navigate to lead with tasks tab, or tasks page
                    const leadId = item.lead?.id;
                    if (leadId) {
                        const isArchived = item.lead?.is_archived;
                        if (isArchived) {
                            navigate(`practice/${practiceId}/archived/${leadId}`);
                        } else {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { goToTaskTab: true },
                            });
                        }
                    } else {
                        navigate('/dashboard/tasks');
                    }
                    break;
                }
                case 'conversations': {
                    // Navigate to WhatsApp thread
                    const threadId = item.thread_id || item.id;
                    if (threadId) {
                        navigate(`practice/${practiceId}/whatsapp/thread/${threadId}`);
                    }
                    break;
                }
                case 'notes': {
                    // Navigate to lead with notes tab
                    const leadId = item.lead?.id || item.lead_id;
                    if (leadId) {
                        const isArchived = item.lead?.is_archived;
                        if (isArchived) {
                            navigate(`practice/${practiceId}/archived/${leadId}`);
                        } else {
                            navigate(`practice/${practiceId}/lead-detail/${leadId}`, {
                                state: { shouldRedirectToOverview: false, tab: 5 },
                            });
                        }
                    }
                    break;
                }
                case 'staff': {
                    // Navigate to practice users page
                    navigate('/dashboard/user/practice-user');
                    break;
                }
                case 'campaigns': {
                    // Navigate to campaign report page
                    navigate(`/dashboard/campaigns-manager/campaigns/${item.id}/report`);
                    saveRecentItem({ ...item, type: 'campaign' });
                    break;
                }
                default:
                    console.warn('Unknown category:', category);
            }
        }
        onClose();
    }, [navigate, practiceId, onClose, saveRecentItem]);

    // Keep handleNavigateRef in sync
    handleNavigateRef.current = handleNavigate;

    const handleCommandExecute = useCallback((cmd) => {
        if (cmd.id === 'new-lead') {
            onClose();
            dispatch(openCreateLeadDialog());
            return;
        }
        if (cmd.action) {
            cmd.action(navigate, practiceId);
        }
        onClose();
    }, [navigate, practiceId, onClose]);

    // Returns action chips for a contextual sub-item (staff / task / campaign)
    const getSubItemActions = useCallback((subItem) => {
        if (!subItem) return [];
        const closePanelFully = () => {
            setActionPanelOpen(false);
            setPanelDepth(0);
            setPanelSubSelected(null);
            setPanelSubItems([]);
        };
        const pad = (n) => String(n).padStart(2, '0');

        if (subItem.__panelType === 'staff') {
            const actions = [];
            if (subItem.is_enabled) {
                actions.push({
                    icon: 'eva:person-delete-fill',
                    label: 'Disable User',
                    color: theme.palette.error.main,
                    onClick: async () => {
                        try {
                            await dispatch(toggleUserEnabled(practiceId, subItem.id, false));
                            enqueueSnackbar('User disabled', { variant: 'success' });
                        } catch {
                            enqueueSnackbar('Failed to disable user', { variant: 'error' });
                        }
                        closePanelFully();
                    },
                });
            } else {
                actions.push({
                    icon: 'eva:person-add-fill',
                    label: 'Enable User',
                    color: theme.palette.success.main,
                    onClick: async () => {
                        try {
                            await dispatch(toggleUserEnabled(practiceId, subItem.id, true));
                            enqueueSnackbar('User enabled', { variant: 'success' });
                        } catch {
                            enqueueSnackbar('Failed to enable user', { variant: 'error' });
                        }
                        closePanelFully();
                    },
                });
            }
            actions.push({
                icon: 'eva:email-fill',
                label: 'Reset Password',
                onClick: async () => {
                    try {
                        await dispatch(resetUserPassword(practiceId, subItem.user?.email));
                        enqueueSnackbar('Password reset email sent', { variant: 'success' });
                    } catch {
                        enqueueSnackbar('Failed to send reset email', { variant: 'error' });
                    }
                },
            });
            actions.push({
                icon: 'eva:external-link-fill',
                label: 'View in Settings',
                onClick: () => { navigate('/dashboard/user/practice-user'); onClose(); },
            });
            return actions;
        }

        if (subItem.__panelType === 'task') {
            const actions = [
                {
                    icon: 'eva:checkmark-circle-2-fill',
                    label: 'Mark Complete',
                    color: theme.palette.success.main,
                    onClick: async () => {
                        await callApi(`/api/v1/practices/${practiceId}/reminders/${subItem.id}/`, 'patch', { is_completed: true });
                        enqueueSnackbar('Task marked complete', { variant: 'success' });
                        closePanelFully();
                    },
                },
                {
                    icon: 'eva:clock-fill',
                    label: 'Snooze 1 Day',
                    onClick: async () => {
                        const d = new Date();
                        d.setDate(d.getDate() + 1);
                        d.setHours(9, 0, 0, 0);
                        const formatted = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:00.0Z`;
                        await callApi(`/api/v1/practices/${practiceId}/reminders/${subItem.id}/`, 'patch', { datetime: formatted });
                        enqueueSnackbar('Snoozed to tomorrow 9am', { variant: 'success' });
                        closePanelFully();
                    },
                },
            ];
            if (subItem.lead?.id) {
                actions.push({
                    icon: 'eva:person-fill',
                    label: `Open ${subItem.lead?.first_name || 'Lead'}`,
                    onClick: () => {
                        navigate(`practice/${practiceId}/lead-detail/${subItem.lead.id}`, { state: { shouldRedirectToOverview: true } });
                        onClose();
                    },
                });
            }
            return actions;
        }

        if (subItem.__panelType === 'campaign') {
            const actions = [
                {
                    icon: 'eva:bar-chart-fill',
                    label: 'View Report',
                    onClick: () => {
                        navigate(`/dashboard/campaigns-manager/campaigns/${subItem.id}/report`);
                        onClose();
                    },
                },
            ];
            if (subItem.status === 'LIVE' || subItem.status === 'SCHEDULED') {
                actions.push({
                    icon: 'eva:close-circle-fill',
                    label: 'Cancel Campaign',
                    color: theme.palette.error.main,
                    onClick: async () => {
                        const res = await callApi(`/api/v1/practices/${practiceId}/campaigns/${subItem.id}/cancel/`, 'post');
                        if (res.ok) {
                            enqueueSnackbar('Campaign cancelled', { variant: 'success' });
                        } else {
                            enqueueSnackbar('Failed to cancel campaign', { variant: 'error' });
                        }
                        closePanelFully();
                    },
                });
            }
            return actions;
        }

        return [];
    }, [practiceId, theme, navigate, onClose, enqueueSnackbar]);

    // Loads sub-items for a command's contextual panel
    const loadContextualPanel = useCallback(async (cmd) => {
        const type = cmd.contextualPanel;
        if (!type) return;
        setActionPanelOpen(true);
        setPanelDepth(1);
        setPanelSubItems([]);
        setPanelSubIndex(0);
        setPanelSubSelected(null);
        setPanelSubLoading(true);
        try {
            if (type === 'staff') {
                // Staff is already loaded in Redux — filter to enabled first
                const items = staffList.map((s) => ({ ...s, __panelType: 'staff' }));
                setPanelSubItems(items);
            } else if (type === 'tasks') {
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/reminders/?is_completed=false&ordering=datetime&limit=15&lead__is_archived=false`
                );
                if (response.ok) {
                    setPanelSubItems((response.data.results || []).map((t) => ({ ...t, __panelType: 'task' })));
                }
            } else if (type === 'campaigns') {
                const response = await callApi(
                    `/api/v1/practices/${practiceId}/campaigns/?status__in=LIVE,SCHEDULED&ordering=-created_at&limit=10`
                );
                if (response.ok) {
                    setPanelSubItems((response.data.results || []).map((c) => ({ ...c, __panelType: 'campaign' })));
                }
            }
        } finally {
            setPanelSubLoading(false);
        }
    }, [staffList, practiceId]);

    // Get actions for the currently selected navigable item (for action panel)
    // Includes contextual actions based on nav type (pinned, recent, search chips)
    const selectedItemActions = useMemo(() => {
        // Depth 2: actions are for the selected contextual sub-item, not the command
        if (panelDepth === 2 && panelSubSelected) {
            return getSubItemActions(panelSubSelected);
        }

        if (!selectedNavItem) return [];

        // Command mode — show a single "Run" action for the selected command
        if (selectedNavItem.__navType === 'command') {
            return [{
                icon: selectedNavItem.icon || 'eva:arrow-right-fill',
                label: selectedNavItem.label,
                onClick: () => handleCommandExecute(selectedNavItem),
            }];
        }

        // Recent search group — show actions for the currently focused chip
        if (selectedNavItem.__navType === 'recentSearchGroup') {
            const selectedChip = (selectedNavItem.searches || [])[chipIndex];
            if (!selectedChip) return [];
            return [
                {
                    icon: 'eva:search-fill',
                    label: `Search "${selectedChip.query}"`,
                    onClick: () => setSearchQuery(selectedChip.query),
                },
                {
                    icon: 'eva:close-circle-fill',
                    label: 'Remove This Search',
                    onClick: () => removeRecentSearch(selectedChip.query),
                },
                {
                    icon: 'eva:trash-2-fill',
                    label: 'Clear All Searches',
                    onClick: clearAllRecentSearches,
                },
            ];
        }

        const cat = selectedNavItem.__navCategory
            || selectedNavItem.__category
            || selectedNavItem.type
            || 'leads';
        const actions = getItemActions(selectedNavItem, cat);

        // Recently viewed — append "Remove from Recent" contextual action
        if (selectedNavItem.__navType === 'recentItem') {
            actions.push({
                icon: 'eva:close-circle-fill',
                label: 'Remove from Recent',
                onClick: () => removeRecentItem(selectedNavItem.id),
            });
        }

        return actions;
    }, [selectedNavItem, getItemActions, chipIndex, handleCommandExecute,
        panelDepth, panelSubSelected, getSubItemActions,
        removeRecentSearch, clearAllRecentSearches, removeRecentItem]);

    // Keyboard navigation — unified across all sections + action panel
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open) return;

            const itemCount = navigableItems.length;

            // When action panel is open, handle its own navigation per depth
            if (actionPanelOpen) {
                if (panelDepth === 1) {
                    // Depth 1: navigating the contextual sub-list
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setPanelSubIndex(prev => (prev + 1) % Math.max(panelSubItems.length, 1));
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setPanelSubIndex(prev => (prev - 1 + panelSubItems.length) % Math.max(panelSubItems.length, 1));
                    } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
                        e.preventDefault();
                        const subItem = panelSubItems[panelSubIndex];
                        if (subItem) {
                            setPanelSubSelected(subItem);
                            setPanelDepth(2);
                            setActionPanelIndex(0);
                        }
                    } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                        e.preventDefault();
                        setActionPanelOpen(false);
                        setPanelDepth(0);
                        setPanelSubItems([]);
                        setPanelSubIndex(0);
                        setPanelSubSelected(null);
                    }
                } else if (panelDepth === 2) {
                    // Depth 2: navigating sub-item action chips
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActionPanelIndex(prev => (prev + 1) % Math.max(selectedItemActions.length, 1));
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActionPanelIndex(prev => (prev - 1 + selectedItemActions.length) % Math.max(selectedItemActions.length, 1));
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        const action = selectedItemActions[actionPanelIndex];
                        if (action) action.onClick();
                    } else if (e.key === 'ArrowLeft') {
                        e.preventDefault();
                        setPanelDepth(1);
                        setPanelSubSelected(null);
                        setActionPanelIndex(0);
                    } else if (e.key === 'Escape') {
                        e.preventDefault();
                        setActionPanelOpen(false);
                        setPanelDepth(0);
                        setPanelSubSelected(null);
                    }
                } else if (panelDepth === 0) {
                    // Depth 0: navigating action chips (existing behaviour)
                    if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActionPanelIndex(prev =>
                            (prev + 1) % Math.max(selectedItemActions.length, 1)
                        );
                    } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActionPanelIndex(prev =>
                            (prev - 1 + selectedItemActions.length) % Math.max(selectedItemActions.length, 1)
                        );
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        const action = selectedItemActions[actionPanelIndex];
                        if (action) action.onClick();
                    } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
                        e.preventDefault();
                        setActionPanelOpen(false);
                        setActionPanelIndex(0);
                    }
                }
                return;
            }

            // Main list navigation
            const isOnSearchGroup = selectedNavItem
                && selectedNavItem.__navType === 'recentSearchGroup';
            const searchGroupChips = isOnSearchGroup
                ? (selectedNavItem.searches || [])
                : [];

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev =>
                    (prev + 1) % Math.max(itemCount, 1)
                );
                setChipIndex(0);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev =>
                    (prev - 1 + itemCount) % Math.max(itemCount, 1)
                );
                setChipIndex(0);
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                if (isOnSearchGroup) {
                    // Navigate chips within the recent searches row
                    setChipIndex(prev =>
                        Math.min(prev + 1, searchGroupChips.length - 1)
                    );
                } else if (selectedNavItem?.__navType === 'command' && selectedNavItem.contextualPanel) {
                    // Command with contextual panel — load sub-list
                    loadContextualPanel(selectedNavItem);
                } else if (selectedNavItem && selectedItemActions.length > 0) {
                    // Open action panel for non-chip items
                    setActionPanelOpen(true);
                    setActionPanelIndex(0);
                    setPanelDepth(0);
                }
            } else if (e.key === 'ArrowLeft') {
                e.preventDefault();
                if (isOnSearchGroup) {
                    setChipIndex(prev => Math.max(prev - 1, 0));
                }
            } else if (e.key === 'Enter') {
                e.preventDefault();
                if (isOnSearchGroup && searchGroupChips[chipIndex]) {
                    setSearchQuery(searchGroupChips[chipIndex].query);
                } else if (selectedNavItem) {
                    if (selectedNavItem.__navType === 'command') {
                        handleCommandExecute(selectedNavItem);
                    } else if (selectedNavItem.__navType === 'pinnedLead') {
                        navigate(`practice/${practiceId}/lead-detail/${selectedNavItem.id}`, {
                            state: { shouldRedirectToOverview: true },
                        });
                        onClose();
                    } else {
                        handleNavigate(selectedNavItem);
                    }
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, selectedIndex, navigableItems, selectedNavItem, selectedItemActions,
        handleNavigate, handleCommandExecute, loadContextualPanel, onClose,
        actionPanelOpen, actionPanelIndex, panelDepth, panelSubItems, panelSubIndex,
        navigate, practiceId, chipIndex,
        isCommandMode]);

    // Reset selected index when search changes, and close action panel
    useEffect(() => {
        setSelectedIndex(0);
        setActionPanelOpen(false);
        setActionPanelIndex(0);
        setChipIndex(0);
        setPanelDepth(0);
        setPanelSubItems([]);
        setPanelSubIndex(0);
        setPanelSubSelected(null);
    }, [searchQuery]);

    // Preview panel disabled — results show full-width

    // Scroll selected item into view
    useEffect(() => {
        if (!open) return;
        const el = document.querySelector('.MuiListItemButton-root.Mui-selected');
        if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [selectedIndex, open]);

    // Render category section with header and results
    // Render treatment result
    const renderTreatmentResult = useCallback((treatment, index, isSelected) => (
        <ListItemButton
            key={treatment.id || `treatment-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(treatment)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="eva:heart-fill"
                        sx={{ color: 'success.main', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                        {treatment.treatment_name}
                    </Typography>
                    {treatment.lead && (
                        <Typography sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            lineHeight: 1.4,
                        }}>
                            Lead: {treatment.lead.first_name} {treatment.lead.last_name}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                    {treatment.custom_price && (
                        <Typography sx={{ fontSize: '12px', fontWeight: 600, color: 'success.main' }}>
                            £{treatment.custom_price}
                        </Typography>
                    )}
                    {treatment.status && (
                        <Box sx={{
                            '& .MuiButton-root': {
                                fontSize: '11px',
                                fontWeight: 600,
                                padding: '2px 8px 2px 2px',
                                minWidth: 'auto',
                                height: '22px',
                                '& svg': { width: 14, height: 14 },
                            },
                        }}>
                            {getStatusButton(treatment.status)}
                        </Box>
                    )}
                    {renderQuickActions(treatment, 'treatments', index)}
                    {renderActionHint(isSelected)}
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, renderQuickActions, renderActionHint, theme, hoveredIndex]);

    // Render appointment result
    const renderAppointmentResult = useCallback((appointment, index, isSelected) => (
        <ListItemButton
            key={appointment.id || `appointment-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(appointment)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="eva:calendar-fill"
                        sx={{ color: 'info.main', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                        {new Date(appointment.datetime).toLocaleString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                        })}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4 }}>
                        {appointment.lead?.first_name} {appointment.lead?.last_name} • {appointment.appointment_type}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                    <Chip
                        label={appointment.status}
                        size="small"
                        sx={{
                            fontSize: '11px',
                            height: 22,
                            bgcolor: (() => {
                                if (appointment.status === 'ATTENDED') return alpha(theme.palette.success.main, 0.12);
                                if (appointment.status === 'BOOKED') return alpha(theme.palette.info.main, 0.12);
                                return alpha(theme.palette.error.main, 0.12);
                            })(),
                            color: (() => {
                                if (appointment.status === 'ATTENDED') return 'success.main';
                                if (appointment.status === 'BOOKED') return 'info.main';
                                return 'error.main';
                            })(),
                        }}
                    />
                    {renderQuickActions(appointment, 'appointments', index)}
                    {renderActionHint(isSelected)}
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, renderQuickActions, renderActionHint, theme, hoveredIndex]);

    // Render task result
    const renderTaskResult = useCallback((task, index, isSelected) => (
        <ListItemButton
            key={task.id || `task-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(task)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="fluent:tasks-app-28-regular"
                        sx={{ color: 'info.main', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                        {task.title || 'Untitled task'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4 }}>
                        {task.lead?.first_name} {task.lead?.last_name}
                        {task.assigned_to_name ? ` · ${task.assigned_to_name}` : ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                    {task.group && (
                        <Chip
                            label={task.group}
                            size="small"
                            sx={{ fontSize: '11px', height: 22 }}
                        />
                    )}
                    {task.priority && task.priority !== 'NORMAL' && (
                        <Chip
                            label={task.priority}
                            size="small"
                            sx={{
                                fontSize: '11px',
                                height: 22,
                                bgcolor: task.priority === 'HIGH'
                                    ? alpha(theme.palette.error.main, 0.12)
                                    : 'default',
                                color: task.priority === 'HIGH' ? 'error.main' : 'default',
                            }}
                        />
                    )}
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, theme]);

    // Render conversation result
    const renderConversationResult = useCallback((conversation, index, isSelected) => (
        <ListItemButton
            key={conversation.id || `conversation-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(conversation)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="eva:message-circle-fill"
                        sx={{ color: 'secondary.main', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                        {conversation.name}
                    </Typography>
                    {conversation.last_message_body && (
                        <Typography sx={{
                            fontSize: '12px',
                            color: 'text.secondary',
                            lineHeight: 1.4,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}>
                            {conversation.last_message_body}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                    {conversation.last_message_time && (
                        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                            {new Date(conversation.last_message_time).toLocaleDateString('en-GB', {
                                day: '2-digit',
                                month: 'short'
                            })}
                        </Typography>
                    )}
                    {conversation.is_closed === false && (
                        <Chip
                            label="Active"
                            size="small"
                            sx={{
                                fontSize: '11px',
                                height: 22,
                                minWidth: 22,
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                color: 'success.main',
                            }}
                        />
                    )}
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, theme]);

    // Render note result
    const renderNoteResult = useCallback((note, index, isSelected) => (
        <ListItemButton
            key={note.id || `note-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(note)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.grey[500], 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="eva:edit-fill"
                        sx={{ color: 'text.secondary', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{
                        fontSize: '12px',
                        color: 'text.secondary',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}>
                        {note.additional_notes_truncated}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: 'text.disabled', lineHeight: 1.4 }}>
                        {note.lead?.first_name} {note.lead?.last_name} • {note.note}
                    </Typography>
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, theme]);

    // Render staff result
    const renderStaffResult = useCallback((staff, index, isSelected) => (
        <ListItemButton
            key={staff.id || `staff-${index}`}
            selected={isSelected}
            onClick={() => handleNavigate(staff)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
            sx={{
                py: 1.5,
                px: 2,
                borderRadius: '8px',
                mb: 0.5,
                '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                    },
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    flexShrink: 0,
                }}>
                    <Iconify
                        icon="eva:people-fill"
                        sx={{ color: 'primary.main', width: 20, height: 20 }}
                    />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                        {staff.first_name} {staff.last_name}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.4 }}>
                        {staff.email}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                    <Chip
                        label={staff.role}
                        size="small"
                        sx={{ fontSize: '11px', height: 22 }}
                    />
                    {renderQuickActions(staff, 'staff', index)}
                    {renderActionHint(isSelected)}
                </Box>
            </Box>
        </ListItemButton>
    ), [handleNavigate, renderQuickActions, renderActionHint, theme, hoveredIndex]);

    const renderLeadItem = useCallback((lead, index, isSelected) => {
        const firstName = lead.first_name || lead.title || '';
        const lastName = lead.last_name || '';
        const leadContact = lead.lead_contact || {};
        const email = leadContact.email || lead.email || '';
        const phone = leadContact.phone || lead.phone || lead.lead_contact_number || '';
        const isArchived = lead.isArchived ?? lead.is_archived;

        // Extract latest treatment
        const leadTreatments = lead.lead_treatments || [];
        const latestTreatment = leadTreatments.length > 0
            ? [...leadTreatments].sort((a, b) =>
                new Date(b.updated_at) - new Date(a.updated_at)
              )[0]
            : null;

        return (
            <ListItemButton
                key={lead.id || `lead-${index}`}
                selected={isSelected}
                onClick={() => handleNavigate(lead)}
                onMouseEnter={() => { setHoveredIndex(index); setSelectedIndex(index); }}
                onMouseLeave={() => setHoveredIndex(null)}
                sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: '8px',
                    mb: 0.5,
                    '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                        },
                    },
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                }}>
                    <Box sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        flexShrink: 0,
                    }}>
                        <Iconify
                            icon="eva:person-fill"
                            sx={{ color: 'primary.main', width: 20, height: 20 }}
                        />
                        {renderNeedsAttentionBadge(lead)}
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{
                            fontSize: '14px',
                            fontWeight: 600,
                            lineHeight: 1.4,
                        }}>
                            {firstName} {lastName}
                        </Typography>
                        {(email || phone) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.25 }}>
                                {email && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                                        <Iconify icon="eva:email-outline" sx={{ width: 14, height: 14, color: 'text.disabled', flexShrink: 0 }} />
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: 'text.secondary',
                                            lineHeight: 1.3,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {email}
                                        </Typography>
                                    </Box>
                                )}
                                {phone && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                        <Iconify icon="eva:phone-outline" sx={{ width: 14, height: 14, color: 'text.disabled' }} />
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: 'text.secondary',
                                            lineHeight: 1.3,
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {phone}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexShrink: 0,
                        ml: 1.5,
                    }}>
                        {latestTreatment && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                            }}>
                                <Typography sx={{
                                    fontSize: '12px',
                                    color: 'text.secondary',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {latestTreatment.treatment}
                                </Typography>
                                <Box sx={{
                                    '& .MuiButton-root': {
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        padding: '2px 8px 2px 2px',
                                        minWidth: 'auto',
                                        height: '22px',
                                        '& svg': {
                                            width: 14,
                                            height: 14,
                                        },
                                    },
                                }}>
                                    {getStatusButton(latestTreatment.status)}
                                </Box>
                            </Box>
                        )}
                        {isArchived && (
                            <Chip
                                label="Archived"
                                size="small"
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.3,
                                    height: 20,
                                    bgcolor: alpha(theme.palette.text.disabled, 0.12),
                                    color: 'text.secondary',
                                    '& .MuiChip-label': { px: 0.875 },
                                }}
                            />
                        )}
                        <Tooltip title={isPinnedLead(lead.leadId || lead.id) ? 'Unpin lead' : 'Pin lead'}>
                            <IconButton
                                size="small"
                                onClick={(e) => togglePinLead(lead, e)}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    '& svg': { width: 16, height: 16 },
                                }}
                            >
                                <Iconify
                                    icon={isPinnedLead(lead.leadId || lead.id) ? 'eva:star-fill' : 'eva:star-outline'}
                                    sx={{
                                        color: isPinnedLead(lead.leadId || lead.id)
                                            ? 'warning.main'
                                            : 'text.secondary',
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                        {renderQuickActions(lead, 'leads', index)}
                        {renderActionHint(isSelected)}
                    </Box>
                </Box>
            </ListItemButton>
        );
    }, [handleNavigate, renderNeedsAttentionBadge, renderLastContacted, renderQuickActions, renderActionHint, isPinnedLead, togglePinLead, searchQuery, theme, hoveredIndex]);

    // Campaign status chip colour
    const getCampaignStatusColor = (status) => {
        switch (status) {
            case 'LIVE': return { bg: alpha('#00AB55', 0.12), color: '#00AB55' };
            case 'SCHEDULED': return { bg: alpha('#078DEE', 0.12), color: '#078DEE' };
            case 'DRAFT': return { bg: alpha('#919EAB', 0.12), color: '#919EAB' };
            case 'COMPLETED': return { bg: alpha('#637381', 0.12), color: '#637381' };
            default: return { bg: alpha('#919EAB', 0.12), color: '#919EAB' };
        }
    };

    // Render campaign result
    const renderCampaignResult = useCallback((campaign, index, isSelected) => {
        const statusColors = getCampaignStatusColor(campaign.status);
        return (
            <ListItemButton
                key={campaign.id || `campaign-${index}`}
                selected={isSelected}
                onClick={() => handleNavigate({ ...campaign, __category: 'campaigns' })}
                sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: '8px',
                    mb: 0.5,
                    '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                        },
                    },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
                    <Box sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '8px',
                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1.5,
                        flexShrink: 0,
                    }}>
                        <Iconify
                            icon="eva:email-fill"
                            sx={{ color: 'warning.main', width: 20, height: 20 }}
                        />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.4 }}>
                            {campaign.name || campaign.title || 'Untitled Campaign'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 1.5 }}>
                        {campaign.status && (
                            <Chip
                                label={campaign.status}
                                size="small"
                                sx={{
                                    fontSize: '11px',
                                    height: 22,
                                    bgcolor: statusColors.bg,
                                    color: statusColors.color,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </ListItemButton>
        );
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNavigate, theme]);

    // Render category section with header and results
    const renderCategorySection = useCallback((category, results, startIndex) => {
        if (!results || results.length === 0) return null;

        const renderResult = (item, localIndex) => {
            const globalIndex = startIndex + localIndex;
            const isSelected = globalIndex === selectedIndex;

            switch (category) {
                case 'leads':
                    return renderLeadItem(item, globalIndex, isSelected);
                case 'treatments':
                    return renderTreatmentResult(item, globalIndex, isSelected);
                case 'appointments':
                    return renderAppointmentResult(item, globalIndex, isSelected);
                case 'tasks':
                    return renderTaskResult(item, globalIndex, isSelected);
                case 'conversations':
                    return renderConversationResult(item, globalIndex, isSelected);
                case 'notes':
                    return renderNoteResult(item, globalIndex, isSelected);
                case 'staff':
                    return renderStaffResult(item, globalIndex, isSelected);
                case 'campaigns':
                    return renderCampaignResult(item, globalIndex, isSelected);
                default:
                    return null;
            }
        };

        return (
            <Box key={category} sx={{ mb: 2 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 2,
                    py: 1,
                    gap: 1,
                }}>
                    <Iconify
                        icon={getCategoryIcon(category)}
                        sx={{ width: 16, height: 16, color: 'text.secondary' }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            flex: 1,
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            fontSize: '11px',
                            letterSpacing: '0.5px',
                        }}
                    >
                        {getCategoryLabel(category)}
                    </Typography>
                    <Chip
                        label={results.length}
                        size="small"
                        sx={{
                            fontSize: '11px',
                            height: 20,
                            minWidth: 20,
                            '& .MuiChip-label': { px: 0.75 },
                        }}
                    />
                </Box>
                <List sx={{ p: 0 }}>
                    {results.map((item, localIndex) => renderResult(item, localIndex))}
                </List>
            </Box>
        );
    }, [selectedIndex, renderLeadItem, renderTreatmentResult, renderAppointmentResult,
        renderTaskResult, renderConversationResult, renderNoteResult, renderStaffResult,
        renderCampaignResult]);

    // Focus the input reliably after Dialog transition completes
    const handleDialogEntered = useCallback(() => {
        inputRef.current?.focus();
    }, []);

    // Compute placeholder outside JSX to avoid nested ternary lint error
    let inputPlaceholder;
    if (isCommandMode) {
        inputPlaceholder = 'Type a command\u2026';
    } else if (activeCategory) {
        inputPlaceholder = `Search ${getCategoryLabel(activeCategory).toLowerCase()}...`;
    } else {
        inputPlaceholder = 'Search leads, treatments, appointments… or type / for commands';
    }

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason === 'escapeKeyDown' && actionPanelOpen) {
                    setActionPanelOpen(false);
                    setActionPanelIndex(0);
                    return;
                }
                onClose();
            }}
            maxWidth="md"
            fullWidth
            TransitionProps={{ onEntered: handleDialogEntered }}
            PaperProps={{
                sx: {
                    borderRadius: '20px',
                    overflow: 'hidden',
                    // Static glow matches shimmer 0%/100% (white) so transition is seamless
                    boxShadow: `
                        0 0 0 0.5px ${alpha('#ffffff', 0.6)},
                        0 0 0 1px ${alpha(theme.palette.common.white, 0.12)},
                        0 0 16px -2px ${alpha('#ffffff', 0.5)},
                        0 0 48px -4px ${alpha('#ffffff', 0.25)},
                        0 0 96px -8px ${alpha('#ffffff', 0.12)},
                        0 16px 32px -12px rgba(0,0,0,0.2),
                        0 4px 12px -4px rgba(0,0,0,0.12),
                        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.18)}
                    `,
                    // Fade in with dialog, then shimmer starts after fully open
                    animation: 'cmdPaletteIn 0.35s ease both, cmdPaletteShimmer 6s ease-in-out 0.5s infinite',
                    '@keyframes cmdPaletteIn': {
                        from: { opacity: 0, transform: 'scale(0.97) translateY(-4px)' },
                        to: { opacity: 1, transform: 'scale(1) translateY(0)' },
                    },
                    '@keyframes cmdPaletteShimmer': {
                        '0%, 100%': {
                            boxShadow: `
                                0 0 0 0.5px ${alpha('#ffffff', 0.6)},
                                0 0 0 1px ${alpha(theme.palette.common.white, 0.12)},
                                0 0 16px -2px ${alpha('#ffffff', 0.5)},
                                0 0 48px -4px ${alpha('#ffffff', 0.25)},
                                0 0 96px -8px ${alpha('#ffffff', 0.12)},
                                0 16px 32px -12px rgba(0,0,0,0.2),
                                0 4px 12px -4px rgba(0,0,0,0.12),
                                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.18)}
                            `,
                        },
                        '50%': {
                            boxShadow: `
                                0 0 0 0.5px ${alpha(theme.palette.primary.main, 0.45)},
                                0 0 0 1px ${alpha(theme.palette.common.white, 0.07)},
                                0 0 16px -2px ${alpha(theme.palette.primary.main, 0.4)},
                                0 0 48px -4px ${alpha(theme.palette.primary.main, 0.22)},
                                0 0 96px -8px ${alpha(theme.palette.primary.main, 0.1)},
                                0 16px 32px -12px rgba(0,0,0,0.2),
                                0 4px 12px -4px rgba(0,0,0,0.12),
                                inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
                            `,
                        },
                    },
                },
            }}
            sx={{
                '& .MuiBackdrop-root': {
                    bgcolor: alpha(theme.palette.grey[900], 0.1),
                    backdropFilter: 'blur(1px)',
                    WebkitBackdropFilter: 'blur(1px)',
                },
            }}
        >
            <Box sx={{ p: 2, pb: 1 }}>
                <TextField
                    inputRef={inputRef}
                    autoFocus
                    fullWidth
                    placeholder={inputPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <>
                                <InputAdornment position="start">
                                    <Iconify
                                        icon="eva:search-fill"
                                        sx={{ color: 'primary.main', width: 24, height: 24 }}
                                    />
                                </InputAdornment>
                                {isCommandMode && (
                                    <InputAdornment position="start">
                                        <Chip
                                            label="CMD"
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                color: 'primary.main',
                                            }}
                                        />
                                    </InputAdornment>
                                )}
                            </>
                        ),
                        endAdornment: (
                            <InputAdornment position="end" sx={{ gap: 0.5 }}>
                                {isLoading && (
                                    <CircularProgress
                                        size={20}
                                        sx={{ color: 'text.secondary' }}
                                    />
                                )}
                                {searchQuery.length > 0 && (
                                    <IconButton
                                        size="small"
                                        onClick={() => setSearchQuery('')}
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            '& svg': { width: 18, height: 18 },
                                        }}
                                    >
                                        <Iconify icon="eva:close-circle-fill" sx={{ color: 'text.disabled' }} />
                                    </IconButton>
                                )}
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            fontSize: '16px',
                            '& fieldset': { borderColor: 'transparent' },
                            '&:hover fieldset': { borderColor: 'transparent' },
                            '&.Mui-focused fieldset': { borderColor: 'transparent' },
                        },
                    }}
                />
            </Box>

            <Divider />

            <Box sx={{
                display: 'flex',
                maxHeight: '400px',
                overflow: 'hidden',
                position: 'relative',
            }}>
                {/* Main content area */}
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 1.5,
                    transition: 'width 200ms ease',
                    width: actionPanelOpen ? '50%' : '100%',
                    minWidth: 0,
                }}>
                    {isCommandMode ? (
                        <Box sx={{ pb: 1 }}>
                            {groupedCommands.length === 0 && commandQuery.length > 0 ? (
                                <Box sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No commands match &quot;{commandQuery}&quot;
                                    </Typography>
                                </Box>
                            ) : (
                                groupedCommands.map(([group, commands]) => (
                                    <Box key={group}>
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                px: 2,
                                                pt: 1.5,
                                                pb: 0.5,
                                                display: 'block',
                                                color: 'text.disabled',
                                                fontSize: '0.65rem',
                                                letterSpacing: '0.08em',
                                            }}
                                        >
                                            {GROUP_LABELS[group] || group}
                                        </Typography>
                                        {commands.map((cmd) => {
                                            const cmdIndex = filteredCommands.findIndex(c => c.id === cmd.id);
                                            const isCmdSelected = cmdIndex === selectedIndex;
                                            return (
                                            <ListItemButton
                                                key={cmd.id}
                                                selected={isCmdSelected}
                                                className={isCmdSelected ? 'Mui-selected' : ''}
                                                onClick={() => handleCommandExecute(cmd)}
                                                sx={{
                                                    px: 2,
                                                    py: 1,
                                                    gap: 1.5,
                                                    borderRadius: 1,
                                                    mx: 1,
                                                    '&:hover': { bgcolor: 'action.hover' },
                                                    '&.Mui-selected': { bgcolor: 'action.selected' },
                                                }}
                                            >
                                                <Iconify
                                                    icon={cmd.icon}
                                                    width={18}
                                                    sx={{ color: 'text.secondary', flexShrink: 0 }}
                                                />
                                                <Typography variant="body2">{cmd.label}</Typography>
                                            </ListItemButton>
                                            );
                                        })}
                                    </Box>
                                ))
                            )}
                        </Box>
                    ) : (
                    <>
                    {/* Overdue task and WhatsApp unread badge chips */}
                    {(overdueTaskCount > 0 || whatsappUnreadCount > 0) && (
                        <Box sx={{ display: 'flex', gap: 1, px: 2, pt: 1.5, pb: 0.5, flexWrap: 'wrap' }}>
                            {overdueTaskCount > 0 && (
                                <Chip
                                    icon={<Iconify icon="eva:clock-fill" width={14} />}
                                    label={`${overdueTaskCount} overdue task${overdueTaskCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    onClick={() => { navigate('/dashboard/tasks'); onClose(); }}
                                    sx={{
                                        bgcolor: alpha('#FF5630', 0.12),
                                        color: '#FF5630',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '& .MuiChip-icon': { color: '#FF5630' },
                                        '&:hover': { bgcolor: alpha('#FF5630', 0.2) },
                                    }}
                                />
                            )}
                            {whatsappUnreadCount > 0 && (
                                <Chip
                                    icon={<Iconify icon="eva:message-circle-fill" width={14} />}
                                    label={`${whatsappUnreadCount} unread message${whatsappUnreadCount !== 1 ? 's' : ''}`}
                                    size="small"
                                    onClick={() => { navigate(`/dashboard/practice/${practiceId}/whatsapp`); onClose(); }}
                                    sx={{
                                        bgcolor: alpha('#078DEE', 0.12),
                                        color: '#078DEE',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        '& .MuiChip-icon': { color: '#078DEE' },
                                        '&:hover': { bgcolor: alpha('#078DEE', 0.2) },
                                    }}
                                />
                            )}
                        </Box>
                    )}

                    {/* Recent searches as compact chips */}
                    {searchQuery.length === 0 && recentSearches.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 2,
                                py: 1,
                            }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'text.secondary',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        fontSize: '11px',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Recent Searches
                                </Typography>
                                <Typography
                                    variant="caption"
                                    onClick={clearAllRecentSearches}
                                    sx={{
                                        color: 'primary.main',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    Clear all
                                </Typography>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                px: 2,
                                py: 0.5,
                                borderRadius: '8px',
                                bgcolor: (selectedNavItem && selectedNavItem.__navType === 'recentSearchGroup')
                                    ? alpha(theme.palette.primary.main, 0.04)
                                    : 'transparent',
                                transition: 'background-color 150ms',
                            }}>
                                {recentSearches.slice(0, MAX_RECENT_SEARCHES).map((search, idx) => {
                                    const isGroupSelected = selectedNavItem
                                        && selectedNavItem.__navType === 'recentSearchGroup';
                                    const isChipSelected = isGroupSelected && chipIndex === idx;
                                    return (
                                        <Chip
                                            key={`search-chip-${idx}`}
                                            label={search.query}
                                            size="small"
                                            onClick={() => setSearchQuery(search.query)}
                                            onDelete={(e) => {
                                                e.stopPropagation();
                                                removeRecentSearch(search.query);
                                            }}
                                            deleteIcon={
                                                <Iconify
                                                    icon="eva:close-outline"
                                                    sx={{ width: 14, height: 14 }}
                                                />
                                            }
                                            sx={{
                                                fontSize: '12px',
                                                height: 28,
                                                borderRadius: '14px',
                                                bgcolor: isChipSelected
                                                    ? alpha(theme.palette.primary.main, 0.16)
                                                    : alpha(theme.palette.grey[500], 0.08),
                                                color: isChipSelected
                                                    ? 'primary.main'
                                                    : 'text.primary',
                                                border: isChipSelected
                                                    ? `1px solid ${alpha(theme.palette.primary.main, 0.4)}`
                                                    : '1px solid transparent',
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                },
                                                '& .MuiChip-deleteIcon': {
                                                    color: 'text.secondary',
                                                    '&:hover': { color: 'error.main' },
                                                },
                                            }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {/* Pinned leads */}
                    {searchQuery.length === 0 && pinnedLeads.length > 0 && (() => {
                        const pinnedStartIndex = recentSearches.length > 0 ? 1 : 0;
                        return (
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    py: 1,
                                    gap: 1,
                                }}>
                                    <Iconify
                                        icon="eva:star-fill"
                                        sx={{ width: 16, height: 16, color: 'warning.main' }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            flex: 1,
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            fontSize: '11px',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        Pinned Leads
                                    </Typography>
                                </Box>
                                <List sx={{ p: 0 }}>
                                    {pinnedLeads.map((pinned, idx) => {
                                        const globalIdx = pinnedStartIndex + idx;
                                        const isPinnedSelected = globalIdx === selectedIndex;
                                        return (
                                            <ListItemButton
                                                key={pinned.id || `pinned-${idx}`}
                                                selected={isPinnedSelected}
                                                onClick={() => {
                                                    navigate(
                                                        `practice/${practiceId}/lead-detail/${pinned.id}`,
                                                        { state: { shouldRedirectToOverview: true } }
                                                    );
                                                    onClose();
                                                }}
                                                sx={{
                                                    py: 1,
                                                    px: 2,
                                                    borderRadius: '8px',
                                                    mb: 0.5,
                                                    '&.Mui-selected': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    width: '100%',
                                                    minWidth: 0,
                                                }}>
                                                    <Box sx={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: '8px',
                                                        bgcolor: alpha(theme.palette.warning.main, 0.08),
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        mr: 1.5,
                                                        flexShrink: 0,
                                                    }}>
                                                        <Iconify
                                                            icon="eva:person-fill"
                                                            sx={{
                                                                color: 'warning.main',
                                                                width: 16,
                                                                height: 16,
                                                            }}
                                                        />
                                                    </Box>
                                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                                        <Typography sx={{
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            lineHeight: 1.4,
                                                        }}>
                                                            {pinned.first_name} {pinned.last_name}
                                                        </Typography>
                                                        {pinned.status && (
                                                            <Typography sx={{
                                                                fontSize: '11px',
                                                                color: 'text.secondary',
                                                                lineHeight: 1.4,
                                                            }}>
                                                                {pinned.status}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Tooltip title="Unpin">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                togglePinLead(pinned, e);
                                                            }}
                                                            sx={{ width: 24, height: 24 }}
                                                        >
                                                            <Iconify
                                                                icon="eva:close-outline"
                                                                sx={{ width: 16, height: 16 }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {renderActionHint(isPinnedSelected)}
                                                </Box>
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            </Box>
                        );
                    })()}

                    {/* Recently viewed leads */}
                    {showRecent && (() => {
                        const recentStartIndex = (recentSearches.length > 0 ? 1 : 0)
                            + pinnedLeads.length;
                        return (
                            <>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    px: 2,
                                    py: 1,
                                }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            fontSize: '11px',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        Recently Viewed
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        onClick={clearAllRecentItems}
                                        sx={{
                                            color: 'primary.main',
                                            fontSize: '11px',
                                            cursor: 'pointer',
                                            '&:hover': { textDecoration: 'underline' },
                                        }}
                                    >
                                        Clear all
                                    </Typography>
                                </Box>
                                <List sx={{ p: 0 }}>
                                    {recentItems.map((item, index) => {
                                        const globalIdx = recentStartIndex + index;
                                        return renderLeadItem(
                                            item,
                                            globalIdx,
                                            globalIdx === selectedIndex
                                        );
                                    })}
                                </List>
                            </>
                        );
                    })()}

                    {/* Category filter indicator */}
                    {activeCategory && searchQuery.length > 0 && (
                        <Box sx={{ px: 2, pb: 1 }}>
                            <Chip
                                label={`Filtering: ${getCategoryLabel(activeCategory)}`}
                                size="small"
                                onDelete={() => setSearchQuery(cleanQuery)}
                                sx={{
                                    fontSize: '11px',
                                    height: 24,
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    color: 'primary.main',
                                }}
                            />
                        </Box>
                    )}

                    {/* Discovery tips for first-time users (like iOS Spotlight) */}
                    {searchQuery.length === 0 && showOnboarding && (
                        <Box sx={{ py: 2, px: 2 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 2,
                            }}>
                                <Typography
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                    }}
                                >
                                    Welcome to Command Palette
                                </Typography>
                                <Typography
                                    variant="caption"
                                    onClick={dismissOnboarding}
                                    sx={{
                                        color: 'primary.main',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        '&:hover': { textDecoration: 'underline' },
                                    }}
                                >
                                    Got it
                                </Typography>
                            </Box>
                            {DISCOVERY_TIPS.map((tip, idx) => (
                                <Box
                                    key={idx}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: 1.5,
                                        mb: 1.5,
                                        p: 1.5,
                                        borderRadius: '10px',
                                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                                        animation: `fadeInTip 300ms ease ${idx * 80}ms both`,
                                        '@keyframes fadeInTip': {
                                            from: { opacity: 0, transform: 'translateY(4px)' },
                                            to: { opacity: 1, transform: 'translateY(0)' },
                                        },
                                    }}
                                >
                                    <Box sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: '8px',
                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                    }}>
                                        <Iconify
                                            icon={tip.icon}
                                            sx={{
                                                width: 16,
                                                height: 16,
                                                color: 'primary.main',
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography sx={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            lineHeight: 1.4,
                                        }}>
                                            {tip.title}
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '12px',
                                            color: 'text.secondary',
                                            lineHeight: 1.4,
                                        }}>
                                            {tip.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Empty state — no query, no recent items, no pinned leads, no searches */}
                    {!showRecent && searchQuery.length === 0
                        && pinnedLeads.length === 0
                        && recentSearches.length === 0
                        && !showOnboarding && (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Iconify
                                icon="eva:search-fill"
                                sx={{
                                    width: 40,
                                    height: 40,
                                    color: 'text.disabled',
                                    mb: 1.5,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', mb: 2 }}
                            >
                                Search leads, treatments, appointments and more…
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.disabled', display: 'block', mb: 0.75 }}
                            >
                                Prefix to filter —{' '}
                                <strong>lead:</strong> leads,{' '}
                                <strong>task:</strong> tasks,{' '}
                                <strong>appt:</strong> appointments
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.disabled', display: 'block', mb: 0.75 }}
                            >
                                Use <strong>arrow keys</strong> to navigate,{' '}
                                <strong>→</strong> to view quick actions for any result
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.disabled', display: 'block' }}
                            >
                                Type <strong>/</strong> to browse commands — navigate, create &amp; settings
                            </Typography>
                        </Box>
                    )}

                    {/* Categorized search results */}
                    {searchQuery.length > 0 && hasCategories && (
                        <>
                            {Object.entries(categorizedResults).map(
                                ([category, results], catIndex) => {
                                    if (!Array.isArray(results) || results.length === 0) {
                                        return null;
                                    }

                                    // Calculate start index for keyboard navigation
                                    let startIndex = 0;
                                    Object.entries(categorizedResults).forEach(
                                        ([, res], idx) => {
                                            if (idx < catIndex && Array.isArray(res)) {
                                                startIndex += res.length;
                                            }
                                        }
                                    );

                                    return renderCategorySection(
                                        category,
                                        results,
                                        startIndex
                                    );
                                }
                            )}
                        </>
                    )}

                    {/* Fallback: lead-only results */}
                    {searchQuery.length > 0 && !hasCategories && hasResults && (
                        <>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 2,
                                    py: 1,
                                    display: 'block',
                                    color: 'text.secondary',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    fontSize: '11px',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                Leads
                            </Typography>
                            <List sx={{ p: 0 }}>
                                {flatResults.map((lead, index) =>
                                    renderLeadItem(lead, index, index === selectedIndex)
                                )}
                            </List>
                        </>
                    )}

                    {/* No results found */}
                    {searchQuery.length > 0 && !hasResults && !isLoading && (
                        <Box sx={{ py: 6, textAlign: 'center' }}>
                            <Iconify
                                icon="eva:search-fill"
                                sx={{
                                    width: 48,
                                    height: 48,
                                    color: 'text.disabled',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary' }}
                            >
                                No results found for &quot;{cleanQuery}&quot;
                            </Typography>
                        </Box>
                    )}
                    </>
                    )}
                </Box>

                {/* Lead preview panel removed — results now show full-width */}

                {/* Action panel — slides in from the right */}
                {actionPanelOpen && selectedNavItem && (
                    <Box
                        sx={{
                            width: '50%',
                            borderLeft: `1px solid ${alpha(theme.palette.grey[500], 0.16)}`,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            animation: 'slideInRight 200ms ease',
                            '@keyframes slideInRight': {
                                from: { transform: 'translateX(100%)', opacity: 0 },
                                to: { transform: 'translateX(0)', opacity: 1 },
                            },
                        }}
                    >
                        {/* ── DEPTH 1: Contextual sub-list ── */}
                        {panelDepth === 1 && (
                            <>
                                {/* Header showing the command */}
                                <Box sx={{ p: 2, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '8px',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <Iconify icon={selectedNavItem.icon || 'eva:grid-fill'} sx={{ color: 'primary.main', width: 16, height: 16 }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 600, lineHeight: 1.3 }}>
                                                {selectedNavItem.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                                {panelSubLoading ? 'Loading…' : `${panelSubItems.length} result${panelSubItems.length !== 1 ? 's' : ''}`}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                {/* Sub-item list */}
                                <Box sx={{ flex: 1, overflowY: 'auto', p: 1 }}>
                                    {panelSubLoading && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                            <CircularProgress size={20} />
                                        </Box>
                                    )}
                                    {!panelSubLoading && panelSubItems.length === 0 && (
                                        <Typography sx={{ fontSize: '12px', color: 'text.secondary', textAlign: 'center', py: 3 }}>
                                            Nothing to show
                                        </Typography>
                                    )}
                                    {!panelSubLoading && panelSubItems.map((subItem, idx) => {
                                        const isSelected = idx === panelSubIndex;
                                        // Render sub-item row by type
                                        let primaryText = '';
                                        let secondaryText = '';
                                        let itemIcon = 'eva:grid-fill';
                                        let statusChip = null;

                                        if (subItem.__panelType === 'staff') {
                                            primaryText = `${subItem.user?.first_name || ''} ${subItem.user?.last_name || ''}`.trim();
                                            secondaryText = subItem.role;
                                            itemIcon = 'eva:people-fill';
                                            statusChip = (
                                                <Chip
                                                    label={subItem.is_enabled ? 'Active' : 'Disabled'}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '10px',
                                                        height: 18,
                                                        bgcolor: subItem.is_enabled
                                                            ? alpha(theme.palette.success.main, 0.12)
                                                            : alpha(theme.palette.error.main, 0.12),
                                                        color: subItem.is_enabled
                                                            ? theme.palette.success.main
                                                            : theme.palette.error.main,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            );
                                        } else if (subItem.__panelType === 'task') {
                                            primaryText = subItem.title || `${subItem.lead?.first_name || ''} ${subItem.lead?.last_name || ''}`.trim();
                                            secondaryText = subItem.datetime_human || subItem.datetime?.slice(0, 10) || '';
                                            itemIcon = 'fluent:tasks-app-28-regular';
                                        } else if (subItem.__panelType === 'campaign') {
                                            primaryText = subItem.name || 'Untitled';
                                            secondaryText = subItem.status;
                                            itemIcon = 'eva:email-fill';
                                            const statusColors = { LIVE: theme.palette.success.main, SCHEDULED: theme.palette.info.main };
                                            const statusColor = statusColors[subItem.status] || theme.palette.text.secondary;
                                            statusChip = (
                                                <Chip
                                                    label={subItem.status}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '10px',
                                                        height: 18,
                                                        bgcolor: alpha(statusColor, 0.12),
                                                        color: statusColor,
                                                        fontWeight: 600,
                                                    }}
                                                />
                                            );
                                        }

                                        return (
                                            <ListItemButton
                                                key={subItem.id || idx}
                                                selected={isSelected}
                                                onClick={() => {
                                                    setPanelSubSelected(subItem);
                                                    setPanelDepth(2);
                                                    setActionPanelIndex(0);
                                                }}
                                                sx={{
                                                    py: 0.75,
                                                    px: 1,
                                                    borderRadius: '8px',
                                                    mb: 0.25,
                                                    '&.Mui-selected': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                                                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                                                    },
                                                }}
                                            >
                                                <Iconify icon={itemIcon} sx={{ width: 16, height: 16, mr: 1, color: isSelected ? 'primary.main' : 'text.secondary', flexShrink: 0 }} />
                                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                                    <Typography sx={{ fontSize: '12px', fontWeight: isSelected ? 600 : 400, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {primaryText}
                                                    </Typography>
                                                    {secondaryText && (
                                                        <Typography sx={{ fontSize: '10px', color: 'text.secondary', lineHeight: 1.2 }}>
                                                            {secondaryText}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {statusChip}
                                                {isSelected && (
                                                    <Iconify icon="eva:arrow-ios-forward-fill" sx={{ width: 14, height: 14, color: 'text.disabled', ml: 0.5, flexShrink: 0 }} />
                                                )}
                                            </ListItemButton>
                                        );
                                    })}
                                </Box>

                                {/* Back hint */}
                                <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip label={'\u2190'} size="small" sx={{ fontSize: '11px', height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>Back</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <Chip label={'\u2192 / Enter'} size="small" sx={{ fontSize: '11px', height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>Actions</Typography>
                                </Box>
                            </>
                        )}

                        {/* ── DEPTH 2: Sub-item action chips ── */}
                        {(panelDepth === 0 || panelDepth === 2) && (() => {
                            // Compute header values once to avoid nested ternaries
                            let headerIcon;
                            let headerTitle;
                            let headerSubtitle;

                            if (panelDepth === 2 && panelSubSelected) {
                                if (panelSubSelected.__panelType === 'staff') {
                                    headerIcon = 'eva:people-fill';
                                    headerTitle = `${panelSubSelected.user?.first_name || ''} ${panelSubSelected.user?.last_name || ''}`.trim();
                                    headerSubtitle = panelSubSelected.role;
                                } else if (panelSubSelected.__panelType === 'campaign') {
                                    headerIcon = 'eva:email-fill';
                                    headerTitle = panelSubSelected.name || 'Campaign';
                                    headerSubtitle = panelSubSelected.status;
                                } else {
                                    headerIcon = 'fluent:tasks-app-28-regular';
                                    headerTitle = panelSubSelected.title || `${panelSubSelected.lead?.first_name || ''} ${panelSubSelected.lead?.last_name || ''}`.trim() || 'Task';
                                    headerSubtitle = panelSubSelected.datetime_human || '';
                                }
                            } else if (selectedNavItem.__navType === 'recentSearchGroup') {
                                headerIcon = 'eva:search-fill';
                                headerTitle = `"${(selectedNavItem.searches || [])[chipIndex]?.query || ''}"`;
                                headerSubtitle = 'Recent search';
                            } else {
                                headerIcon = getCategoryIcon(selectedNavItem.__navCategory || selectedNavItem.__category || 'leads');
                                headerTitle = `${selectedNavItem.first_name || selectedNavItem.title || selectedNavItem.treatment_name || selectedNavItem.name || ''} ${selectedNavItem.last_name || ''}`.trim();
                                const navEmail = selectedNavItem.lead_contact?.email || selectedNavItem.email || '';
                                const navPhone = selectedNavItem.lead_contact?.phone || selectedNavItem.phone || selectedNavItem.lead_contact_number || '';
                                headerSubtitle = [navEmail, navPhone].filter(Boolean).join(' \u00B7 ');
                            }

                            return (
                            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                                {/* Header */}
                                <Box sx={{ mb: 2, pb: 1.5, borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Box sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: '8px',
                                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0,
                                        }}>
                                            <Iconify icon={headerIcon} sx={{ color: 'primary.main', width: 18, height: 18 }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, flex: 1 }}>
                                            <Typography sx={{ fontSize: '14px', fontWeight: 600, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {headerTitle}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                                {headerSubtitle}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {/* Journey stage chip — only for lead-type items with treatment data */}
                                    {panelDepth === 0 && selectedNavItem.__navType !== 'recentSearchGroup' && (() => {
                                        const cat = selectedNavItem.__navCategory || selectedNavItem.__category || selectedNavItem.type;
                                        if (cat !== 'leads' && cat !== 'lead') return null;
                                        const treatments = selectedNavItem.lead_treatments || [];
                                        const currentStatus = treatments.length > 0 ? getLeadCurrentStatus(treatments) : (selectedNavItem.status || 'NEW');
                                        const stage = getLeadStage(currentStatus);
                                        return (
                                            <Chip
                                                label={stage.label}
                                                size="small"
                                                sx={{
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    height: 22,
                                                    borderRadius: '6px',
                                                    bgcolor: alpha(stage.color, 0.12),
                                                    color: stage.color,
                                                }}
                                            />
                                        );
                                    })()}
                                </Box>

                                {/* Sectioned action list */}
                                {(() => {
                                    const hasNext = selectedItemActions.some((a) => a.section === 'next');
                                    return (
                                        <>
                                            {hasNext && (
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px', mb: 0.75, display: 'block' }}>
                                                    Next Steps
                                                </Typography>
                                            )}
                                            <List sx={{ p: 0 }}>
                                                {selectedItemActions.map((action, idx) => {
                                                    const showMoreLabel = hasNext && action.section !== 'next' && (idx === 0 || selectedItemActions[idx - 1]?.section === 'next');
                                                    return (
                                                        <Box key={`action-${idx}`}>
                                                            {showMoreLabel && (
                                                                <>
                                                                    <Divider sx={{ my: 1 }} />
                                                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '10px', letterSpacing: '0.5px', mb: 0.75, display: 'block', px: 0.5 }}>
                                                                        More Actions
                                                                    </Typography>
                                                                </>
                                                            )}
                                                            <ListItemButton
                                                                selected={idx === actionPanelIndex}
                                                                onClick={action.onClick}
                                                                sx={{
                                                                    py: 0.75,
                                                                    px: 1.5,
                                                                    borderRadius: '8px',
                                                                    mb: 0.25,
                                                                    '&.Mui-selected': {
                                                                        bgcolor: action.color ? alpha(action.color, 0.08) : alpha(theme.palette.primary.main, 0.08),
                                                                        '&:hover': { bgcolor: action.color ? alpha(action.color, 0.12) : alpha(theme.palette.primary.main, 0.12) },
                                                                    },
                                                                }}
                                                            >
                                                                <Iconify icon={action.icon} sx={{ width: 18, height: 18, mr: 1.5, color: action.color || (idx === actionPanelIndex ? 'primary.main' : 'text.secondary') }} />
                                                                <Typography sx={{ fontSize: '13px', fontWeight: idx === actionPanelIndex ? 600 : 400, color: (action.section === 'next' && action.color) ? action.color : 'text.primary' }}>
                                                                    {action.label}
                                                                </Typography>
                                                            </ListItemButton>
                                                        </Box>
                                                    );
                                                })}
                                            </List>
                                        </>
                                    );
                                })()}

                                {/* Back hint */}
                                <Box sx={{ mt: 'auto', pt: 1, borderTop: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Chip label={'\u2190'} size="small" sx={{ fontSize: '11px', height: 20, '& .MuiChip-label': { px: 0.5 } }} />
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                        {panelDepth === 2 ? 'Back to list' : 'Back'}
                                    </Typography>
                                </Box>
                            </Box>
                            );
                        })()}
                    </Box>
                )}
            </Box>

            <Divider />

            <Box sx={{
                px: 2, py: 1.5,
                bgcolor: alpha(theme.palette.grey[500], 0.04),
            }}>
                {actionPanelOpen ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        gap: 2, flexWrap: 'wrap',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label={'\u2191\u2193'}
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Choose action
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label="Enter"
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Run action
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label={'\u2190'}
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Back to results
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        gap: 2, flexWrap: 'wrap',
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label={'\u2191\u2193'}
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Navigate
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label={'\u2192'}
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                View actions
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label="Enter"
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Open
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip
                                label="Esc"
                                size="small"
                                sx={{
                                    fontSize: '11px', height: 20,
                                    '& .MuiChip-label': { px: 0.5 },
                                }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary', fontSize: '11px' }}
                            >
                                Close
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};

export default memo(CommandPalette);
